import type { Tab } from './index';
import { esc, timeAgo } from '../utils';
import { workerFetch } from '../worker';
import { showToast } from '../toast';
import { store } from '../state';
import type { Ticker } from '../state';

interface SentArticle {
  headline:   string;
  score:      number;
  label:      string;
  created_at: string;
  url:        string;
}

interface SentResult {
  ticker:   string;
  score:    number;
  label:    string;
  count:    number;
  articles: SentArticle[];
}

interface SentResponse {
  results:  SentResult[];
  cachedAt: string;
}

const LABEL_COLOR: Record<string, string> = {
  Bullish: 'var(--green)',
  Bearish: 'var(--red)',
  Neutral: 'var(--text3)',
};

function scoreGauge(score: number, size = 56): string {
  const col   = score > 0.1 ? 'var(--green)' : score < -0.1 ? 'var(--red)' : 'var(--warn)';
  const pct   = Math.round(((score + 1) / 2) * 100);
  const r     = (size / 2) - 5;
  const circ  = 2 * Math.PI * r;
  const dash  = (pct / 100) * circ;
  return `
    <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" style="display:block;">
      <circle cx="${size/2}" cy="${size/2}" r="${r}" fill="none" stroke="var(--border)" stroke-width="4"/>
      <circle cx="${size/2}" cy="${size/2}" r="${r}" fill="none" stroke="${col}" stroke-width="4"
        stroke-dasharray="${dash} ${circ}" stroke-dashoffset="${circ * 0.25}"
        stroke-linecap="round" transform="rotate(-90 ${size/2} ${size/2})"/>
      <text x="${size/2}" y="${size/2 + 4}" text-anchor="middle"
        style="font-family:var(--mono);font-size:11px;fill:${col};font-weight:700;">
        ${score > 0 ? '+' : ''}${score.toFixed(2)}
      </text>
    </svg>`;
}

export class SentimentTab implements Tab {
  readonly id = 'sentiment';
  private currentTickers: string[] = [];
  private allArticles: (SentArticle & { ticker: string })[] = [];
  private activeLabel: string = 'All';

  private el(id: string): HTMLElement { return document.getElementById(id) as HTMLElement; }

  init(): void {
    this.el('sent-analyze-btn').addEventListener('click', () => this.analyzeInput());
    this.el('sent-refresh-btn').addEventListener('click', () => this.load(this.currentTickers));
    this.el('sent-portfolio-btn').addEventListener('click', () => this.loadPortfolio());

    (this.el('sent-ticker-input') as HTMLInputElement)
      .addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter') this.analyzeInput();
      });

    this.el('sent-articles').addEventListener('click', (e: MouseEvent) => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-sent-label]');
      if (btn) {
        this.activeLabel = btn.dataset.sentLabel!;
        this.renderArticles();
        this.el('sent-articles').querySelectorAll<HTMLElement>('[data-sent-label]').forEach(b => {
          b.style.background = b.dataset.sentLabel === this.activeLabel
            ? 'rgba(0,212,255,0.15)' : 'transparent';
        });
      }
    });
  }

  render(): void {
    const hasKeys = !!(localStorage.getItem('meridian_alpaca_key') && localStorage.getItem('meridian_alpaca_secret'));
    if (!hasKeys) {
      this.showConfigError('Alpaca API keys required — add them in Settings to enable sentiment analysis.');
      return;
    }
    if (!this.currentTickers.length) this.loadPortfolio();
  }

  private analyzeInput(): void {
    const raw = (this.el('sent-ticker-input') as HTMLInputElement).value.trim().toUpperCase();
    if (!raw) return;
    const tickers = raw.split(',').map(t => t.trim()).filter(Boolean).slice(0, 6);
    this.load(tickers);
  }

  private loadPortfolio(): void {
    const tickers = (store.get('tickers') as Ticker[])
      .map(h => (h.Ticker ?? '').toUpperCase().trim())
      .filter(Boolean)
      .slice(0, 6);

    if (!tickers.length) {
      showToast('No portfolio tickers — enter tickers manually or sync Holdings', 'warn');
      return;
    }
    (this.el('sent-ticker-input') as HTMLInputElement).value = tickers.join(', ');
    this.load(tickers);
  }

  private showConfigError(msg: string): void {
    this.el('sent-macro-grid').innerHTML = `
      <div class="empty-state">
        <div style="font-size:22px;margin-bottom:8px;">⚙</div>
        <div style="font-family:var(--mono);font-size:12px;color:var(--warn);">${esc(msg)}</div>
        <div style="font-size:11px;color:var(--text3);margin-top:6px;">Open Settings with <kbd style="background:var(--bg3);border:1px solid var(--border);padding:1px 5px;border-radius:2px;font-family:var(--mono);">Ctrl+Shift+,</kbd></div>
      </div>`;
    this.el('sent-articles').innerHTML = '';
  }

  private async load(tickers: string[]): Promise<void> {
    this.currentTickers = tickers;
    const query = tickers.length ? tickers.join(',') : 'market';

    const alpacaKey    = localStorage.getItem('meridian_alpaca_key')    ?? '';
    const alpacaSecret = localStorage.getItem('meridian_alpaca_secret') ?? '';

    if (!alpacaKey || !alpacaSecret) {
      this.showConfigError('Alpaca API keys required — add them in Settings to enable sentiment analysis.');
      return;
    }

    this.el('sent-macro-grid').innerHTML = `
      <div style="grid-column:1/-1;padding:20px;color:var(--text3);font-family:var(--mono);font-size:12px;">
        Loading sentiment for ${esc(query)}…
      </div>`;
    this.el('sent-articles').innerHTML = '';

    try {
      const data = await workerFetch<SentResponse>('/api/sentiment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tickers: query, alpacaKey, alpacaSecret }),
        signal: AbortSignal.timeout(15000),
      });
      this.el('sent-updated').textContent = `Cached ${timeAgo(data.cachedAt)}`;

      this.renderMacroGrid(data.results);

      // Flatten all articles with source ticker
      this.allArticles = data.results.flatMap(r =>
        r.articles.map(a => ({ ...a, ticker: r.ticker }))
      );
      this.allArticles.sort((a, b) => Math.abs(b.score) - Math.abs(a.score));
      this.activeLabel = 'All';
      this.renderArticles();
    } catch (err: unknown) {
      const raw = err instanceof Error ? err.message : String(err);
      const msg = raw.toLowerCase().includes('networkerror') || raw.toLowerCase().includes('failed to fetch')
        ? 'Network error — check your Worker URL in Settings (Ctrl+Shift+,)'
        : raw;
      this.el('sent-macro-grid').innerHTML =
        `<div style="grid-column:1/-1;padding:20px;color:var(--red);font-family:var(--mono);font-size:12px;">Error: ${esc(msg)}</div>`;
    }
  }

  private renderMacroGrid(results: SentResult[]): void {
    const grid = this.el('sent-macro-grid');
    if (!results.length) { grid.innerHTML = ''; return; }

    grid.innerHTML = results.map(r => {
      const col   = LABEL_COLOR[r.label] ?? 'var(--text3)';
      const gauge = scoreGauge(r.score, 52);
      return `
        <div style="background:var(--panel);border:1px solid var(--border);border-left:3px solid ${col};border-radius:4px;padding:14px;display:flex;gap:12px;align-items:center;">
          <div>${gauge}</div>
          <div style="min-width:0;">
            <div style="font-family:var(--mono);font-size:14px;font-weight:700;color:var(--accent);">${esc(r.ticker)}</div>
            <div style="font-size:11px;font-weight:700;color:${col};letter-spacing:0.06em;margin-top:2px;">${esc(r.label)}</div>
            <div style="font-size:10px;color:var(--text3);margin-top:2px;">${r.count} articles</div>
          </div>
        </div>`;
    }).join('');
  }

  private renderArticles(): void {
    const container = this.el('sent-articles');
    const filtered  = this.activeLabel === 'All'
      ? this.allArticles
      : this.allArticles.filter(a => a.label === this.activeLabel);

    if (!this.allArticles.length) { container.innerHTML = ''; return; }

    const labels = ['All', 'Bullish', 'Neutral', 'Bearish'];
    const counts: Record<string, number> = { All: this.allArticles.length };
    ['Bullish', 'Neutral', 'Bearish'].forEach(l => {
      counts[l] = this.allArticles.filter(a => a.label === l).length;
    });

    const filterBar = labels.map(l => {
      const col = LABEL_COLOR[l] ?? 'var(--text3)';
      const active = l === this.activeLabel;
      return `<button data-sent-label="${esc(l)}"
        style="background:${active ? 'rgba(0,212,255,0.15)' : 'transparent'};
               border:1px solid var(--border);color:${l === 'All' ? 'var(--text2)' : col};
               font-family:var(--mono);font-size:10px;padding:4px 10px;cursor:pointer;
               border-radius:2px;letter-spacing:0.06em;">
        ${esc(l)} <span style="color:var(--text3);">(${counts[l] ?? 0})</span>
      </button>`;
    }).join('');

    const rows = filtered.map(a => {
      const col  = LABEL_COLOR[a.label] ?? 'var(--text3)';
      const sign = a.score > 0 ? '+' : '';
      return `
        <a href="${esc(a.url)}" target="_blank" rel="noopener"
          style="display:flex;gap:12px;align-items:flex-start;padding:11px 14px;
                 background:var(--panel);border:1px solid var(--border);
                 border-left:3px solid ${col};border-radius:3px;margin-bottom:5px;
                 text-decoration:none;color:inherit;">
          <div style="flex-shrink:0;min-width:54px;text-align:center;">
            <div style="font-family:var(--mono);font-size:11px;font-weight:700;color:var(--accent);">${esc(a.ticker)}</div>
            <div style="font-size:10px;color:${col};font-weight:700;margin-top:2px;">${esc(a.label)}</div>
            <div style="font-size:10px;color:${col};margin-top:1px;">${sign}${a.score.toFixed(2)}</div>
          </div>
          <div style="flex:1;min-width:0;">
            <div style="font-size:13px;color:var(--text);line-height:1.4;margin-bottom:4px;">${esc(a.headline)}</div>
            <div style="font-size:10px;color:var(--text3);font-family:var(--mono);">${timeAgo(a.created_at)}</div>
          </div>
        </a>`;
    }).join('');

    container.innerHTML = `
      <div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:12px;">${filterBar}</div>
      ${filtered.length
        ? rows
        : `<div class="empty-state">No ${this.activeLabel.toLowerCase()} articles found.</div>`
      }`;
  }
}
